import { useMemo, useState } from 'react';
import type { Folder } from '../../api/folders.js';

/**
 * MoveDialog — pick a destination folder.
 *
 * In-app replacement for the window.prompt folder-name picker (native prompts
 * are blocked in the cross-origin embed iframe). Shows the customer's folders
 * as an indented list plus "Top level". When moving a folder, its own subtree
 * is excluded so it can't be dropped inside itself.
 */
export interface MoveDialogProps {
  title: string;
  folders: ReadonlyArray<Folder>;
  /** The item's current parent (preselected). */
  currentParentId: string | null;
  /** Moving a folder: exclude it and its descendants from the choices. */
  excludeSubtreeOf?: string | undefined;
  onSubmit(destParentId: string | null): void | Promise<void>;
  onCancel(): void;
}

export function MoveDialog(props: MoveDialogProps): JSX.Element {
  const { title, folders, currentParentId, excludeSubtreeOf, onSubmit, onCancel } = props;
  const [dest, setDest] = useState<string>(currentParentId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(() => {
    const excluded = new Set<string>();
    if (excludeSubtreeOf) {
      const addSubtree = (id: string): void => {
        excluded.add(id);
        folders.filter((f) => f.parentId === id).forEach((c) => addSubtree(c.id));
      };
      addSubtree(excludeSubtreeOf);
    }
    const byParent = new Map<string | null, Folder[]>();
    for (const f of folders) {
      if (excluded.has(f.id)) continue;
      const key = f.parentId ?? null;
      const arr = byParent.get(key) ?? [];
      arr.push(f);
      byParent.set(key, arr);
    }
    const out: Array<{ id: string; label: string }> = [];
    const walk = (parentId: string | null, depth: number): void => {
      const kids = (byParent.get(parentId) ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name));
      for (const k of kids) {
        out.push({ id: k.id, label: `${' '.repeat(depth)}${k.name}` });
        walk(k.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [folders, excludeSubtreeOf]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(dest === '' ? null : dest);
    } catch (err) {
      setError((err as Error).message || 'Move failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <form
        className="modal modal--prompt"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        aria-labelledby="move-title"
      >
        <header className="modal-header">
          <div>
            <h2 id="move-title">{title}</h2>
          </div>
          <button
            type="button"
            className="btn-icon"
            onClick={onCancel}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>
        <div className="modal-body">
          <label className="form-label" htmlFor="move-dest">
            Destination folder
          </label>
          <select
            id="move-dest"
            className="form-input"
            value={dest}
            onChange={(e) => setDest(e.target.value)}
          >
            <option value="">Top level</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {error && <div className="form-hint form-hint--error">{error}</div>}
        </div>
        <footer className="modal-footer">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? 'Moving…' : 'Move'}
          </button>
        </footer>
      </form>
    </div>
  );
}
